import { useCallback, useEffect, useState } from 'react';
import { formatDate } from '@jywer/ui';

/**
 * V1.4 scope V.2 — Abonnementsudløb section at the top of
 * `/admin/billing`. Lists pending `subscription_end_tasks` with a
 * "Marker som udført" action per row. Daniel manages these manually
 * when closing subs in Dinero — no automation, no CSV export in V1.4.
 */

const SUB_TYPE_LABEL: Record<string, string> = {
  basic_hosting_support: 'Basic abo',
  seo_aeo: 'SEO/AEO',
  google_ads: 'Google Ads',
  meta_ads: 'Meta',
  linkedin_ads: 'LinkedIn Ads',
  linkedin_profilpleje: 'LinkedIn Profilpleje',
  maas: 'MaaS',
  other: 'Andet',
};

interface Row {
  id: string;
  subscription_id: string;
  client_id: string;
  client_name: string;
  subscription_type: string;
  end_date: string;
  status: 'pending' | 'done' | 'cancelled';
}

export function SubscriptionEndTasks() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/billing/subscription-end-tasks');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { rows: Row[] };
      setRows(json.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const markDone = async (id: string) => {
    try {
      const res = await fetch(`/api/billing/subscription-end-tasks/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status: 'done' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (loading && rows.length === 0) {
    return (
      <section className="rounded border border-line-1 bg-paper-0 px-4 py-3 text-sm text-ink-2">
        Henter abonnementsudløb…
      </section>
    );
  }

  if (rows.length === 0 && !error) return null;

  return (
    <section className="rounded border border-status-warn/40 bg-status-warn-tint/60 p-4">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-status-warn">Abonnementsudløb ({rows.length})</h2>
        <button
          type="button"
          onClick={() => void reload()}
          className="text-xs text-status-warn underline hover:no-underline"
        >
          Genindlæs
        </button>
      </header>
      {error ? <p className="mb-2 text-xs text-status-err">{error}</p> : null}
      <ul className="flex flex-col gap-1">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex items-center gap-3 rounded border border-status-warn/40 bg-paper-0 px-3 py-2 text-sm"
          >
            <span className="min-w-0 flex-1 truncate font-medium text-ink-0">{r.client_name}</span>
            <span className="shrink-0 rounded-full border border-line-2 bg-paper-0 px-2 py-0.5 text-xs text-ink-1">
              {SUB_TYPE_LABEL[r.subscription_type] ?? r.subscription_type}
            </span>
            <span className="shrink-0 tabular-nums text-xs text-ink-2">
              udløber {formatDate(r.end_date)}
            </span>
            <button
              type="button"
              onClick={() => void markDone(r.id)}
              className="shrink-0 rounded bg-primary px-2.5 py-1 text-xs font-medium text-white hover:bg-primary/90"
            >
              Marker som udført
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
